import axios, { CancelTokenSource } from 'axios';
import _ from 'lodash';
import { FunctionComponent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import buildingIcon from '../../assets/images/building-icon.png';
import lookupIcon from '../../assets/images/lookup-icon.png';
import MainLoader from '../../components/shared/MainLoader';
import IPropertyInfo from '../../components/shared/PropertiesMap/iPropertyInfo';
import PropertiesMap from '../../components/shared/PropertiesMap/PropertiesMap';
import RouteParams from '../../interfaces/general/Route';
import TransactionDataService from '../../services/TransactionDataService';
import FilterMenuPanel from './components/FilterMenuPanel';
import SearchMenuPanel from './components/SearchMenuPanel';
import { SearchTransactionDataModel } from './models/search-transaction-data.model';
import { TransactionDataInfo } from './models/transaction-data-info.model';

const MapFullHeight = '100%';
const getErrorMessage = (error: any) => {
    if (error?.response?.status === 504) {
        return 'Request timed out (504). Please try again.';
    }

    return error?.response?.data?.errorMessage || error?.message || 'Unexpected error';
};

const SearchingMap: FunctionComponent = ({ children }) => {
    const { t } = useTranslation(['f', 'b']),
        history = useHistory(),
        { id } = useParams<RouteParams>(),
        [selectedMenu, setSelectedMenu] = useState<number>(0),
        [mapHeight, setMapHeight] = useState<string>(MapFullHeight),
        [isLoading, setIsLoading] = useState<boolean>(false),
        [transactionDataList, setTransactionData] = useState<Array<TransactionDataInfo>>([]);

    const cancelTokenSourceRef = useRef<CancelTokenSource | null>(null);
    const responseCacheRef = useRef<Map<string, Array<TransactionDataInfo>>>(new Map());

    const createFilterKey = (params?: SearchTransactionDataModel): string => {
        if (!params) {
            return 'all';
        }

        return JSON.stringify({
            ...params,
            sectors: params.sectors ? [...params.sectors].sort() : params.sectors
        });
    };

    const fetchAllTransactionData = useCallback(async (params?: SearchTransactionDataModel) => {
        const filterKey = createFilterKey(params);
        const cachedData = responseCacheRef.current.get(filterKey);
        if (cachedData) {
            setTransactionData(cachedData);
            return;
        }

        cancelTokenSourceRef.current?.cancel('Cancel previous fetch Transaction Data');
        const nextCancelTokenSource = axios.CancelToken.source();
        cancelTokenSourceRef.current = nextCancelTokenSource;
        setIsLoading(true);

        try {
            const transactionData = await TransactionDataService.fecthAllTransactionData(nextCancelTokenSource.token, params);
            responseCacheRef.current.set(filterKey, transactionData);
            setTransactionData(transactionData);
        } catch (error: any) {
            if (axios.isCancel(error)) {
                return;
            }

            toast(t(`b:${getErrorMessage(error)}`));
        } finally {
            if (cancelTokenSourceRef.current === nextCancelTokenSource) {
                setIsLoading(false);
            }
        }
    }, [t]);

    const filterTransactionData = useMemo(
        () => _.debounce((params: SearchTransactionDataModel) => {
            fetchAllTransactionData(params);
        }, 250),
        [fetchAllTransactionData]
    );

    useEffect(() => {
        fetchAllTransactionData();
    }, [fetchAllTransactionData]);

    useEffect(() => {
        return () => {
            filterTransactionData.cancel();
            cancelTokenSourceRef.current?.cancel('Cancel fetch Transaction Data on unmount');
        };
    }, [filterTransactionData]);

    useEffect(() => {
        setMapHeight(id ? 'calc(50vh - 30px)' : MapFullHeight);
    }, [id]);

    const handleTransactionClicked = (transaction: IPropertyInfo) => {
        history.push(`/transaction-data/${transaction.id}`);
    };

    return (
        <div className="property-hero searching-map">
            <figure>
                <div className="background-cover">
                    <div className='navigation-icon'>
                        <img src={buildingIcon} onClick={() => setSelectedMenu(0)} />
                        <hr />
                        <img src={lookupIcon} onClick={() => setSelectedMenu(1)} />
                    </div>
                    <div className='search-content'>
                        <div className='search-panel'>
                            <div style={{ display: selectedMenu === 0 ? 'block' : 'none' }}>
                                <FilterMenuPanel filter={filterTransactionData}/>
                            </div>
                            <div className='search-menu' style={{ display: selectedMenu === 1 ? 'block' : 'none' }}>
                                <SearchMenuPanel
                                    allTransactionData={transactionDataList}
                                    onTransactionClicked={(transaction) => handleTransactionClicked(transaction)}
                                />
                            </div>
                        </div>
                        <div className="map-panel">
                            <PropertiesMap type={TransactionDataInfo} height={mapHeight} markerId={id} properties={transactionDataList} onMarkerClicked={handleTransactionClicked} />
                            {!isLoading ? null : <div className='loading'>
                                <MainLoader type="regular" size={100} />
                            </div>}
                            {children}
                        </div>
                    </div>
                </div>
            </figure>
        </div>
    );
};

export default SearchingMap;
